import React from 'react';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getCataloguePath } from '../features/training/trainingRoutes';
import { getMentoringContent } from '../features/mentoring/mentoringContent';
import { getMentoringPath } from '../features/mentoring/mentoringRoutes';
import { BUILDER_URL } from '../lib/externalLinks';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const locale = i18n.language.startsWith('en') ? 'en' : 'fr';
  const trainingCataloguePath = getCataloguePath(locale);
  const mentoring = getMentoringContent(locale);
  const mentoringPath = getMentoringPath(locale, 'index');

  const services = [
    { name: t('builder.footerLabel'), href: BUILDER_URL, isExternal: true },
    { name: t('footer.services.learningPlatform'), href: 'https://kursa.csl-brands.com' },
    { name: t('training.footerLabel'), to: trainingCataloguePath, isRoute: true },
    ...(mentoringPath ? [{ name: mentoring?.nav.brand, to: mentoringPath, isRoute: true }] : []),
    { name: t('footer.services.primecorn'), href: 'https://erp.csl-brands.com' },
    { name: t('footer.services.tchopnjoh'), href: 'https://tchopnjoh.csl-brands.com' }
  ];

  const quickLinks = [
    { name: t('footer.quickLinks.about'), href: '#values' },
    { name: t('training.navLabel'), to: trainingCataloguePath, isRoute: true },
    ...(mentoringPath
      ? [{ name: mentoring?.nav.brand, to: mentoringPath, isRoute: true }]
      : []),
    { name: t('footer.quickLinks.careers'), href: '#' },
    { name: t('footer.quickLinks.blog'), href: '/#insights' },
    { name: t('footer.quickLinks.contact'), href: '#contact' },
    { name: t('footer.quickLinks.helpCenter'), href: 'https://resources.csl-brands.com/' }
  ];

  const socialLinks = [
    { name: 'LinkedIn', href: 'https://www.linkedin.com/company/kursa-brands/', icon: (props) => <svg fill="currentColor" viewBox="0 0 24 24" {...props}><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg> },
    { name: 'Facebook', href: 'https://web.facebook.com/getkursa/', icon: (props) => <svg fill="currentColor" viewBox="0 0 24 24" {...props}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg> },
  ];

  return (
    <footer className="bg-gray-900 text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img
                src="/favicon.svg"
                alt="CSL Brands Logo"
                className="w-16 h-10 object-contain"
              />
              <span className="text-2xl font-bold font-futura">CSL Brands</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            {mentoringPath && (
              <Link
                to={mentoringPath}
                className="inline-flex items-center gap-2 mb-6 px-3 py-2 border border-csl-green rounded-lg text-csl-green hover:bg-csl-green hover:text-white transition-colors duration-300 text-sm font-semibold"
              >
                {mentoring?.nav.brand}
                <span aria-hidden="true">→</span>
              </Link>
            )}
            <div className="flex space-x-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-400 hover:text-csl-green transition-colors duration-300 transform hover:scale-110"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-csl-green">{t('footer.servicesTitle')}</h3>
            <ul className="space-y-4">
              {services.map((item) => (
                <li key={item.name}>
                  {item.isRoute ? (
                    <Link
                      to={item.to}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-1.5 h-1.5 bg-csl-green rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      target={item.isExternal ? '_blank' : undefined}
                      rel={item.isExternal ? 'noopener noreferrer' : undefined}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-1.5 h-1.5 bg-csl-green rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-csl-green">{t('footer.quickLinksTitle')}</h3>
            <ul className="space-y-4">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  {item.isRoute ? (
                    <Link
                      to={item.to}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-1.5 h-1.5 bg-csl-green rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      target={item.isExternal ? '_blank' : undefined}
                      rel={item.isExternal ? 'noopener noreferrer' : undefined}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-1.5 h-1.5 bg-csl-green rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-csl-green">{t('footer.contactTitle')}</h3>
            <ul className="space-y-4">
              <li className="flex items-center text-gray-400 group">
                <EnvelopeIcon className="h-5 w-5 mr-3 text-csl-green group-hover:animate-pulse" />
                <a href="mailto:contact@csl-brands.com" className="hover:text-white transition-colors">
                  contact@csl-brands.com
                </a>
              </li>
              <li className="flex items-center text-gray-400 group">
                <GlobeAltIcon className="h-5 w-5 mr-3 text-csl-green group-hover:animate-spin-slow" />
                <a href="https://csl-brands.com" className="hover:text-white transition-colors">
                  www.csl-brands.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            &copy; 2024 - {currentYear} CSL Brands. {t('footer.copyright')}
          </p>
          <div className="flex space-x-6 text-sm text-gray-400">
            <a href="/privacy-policy" className="hover:text-white transition-colors duration-300">{t('footer.privacy')}</a>
            <a href="/terms-of-service" className="hover:text-white transition-colors duration-300">{t('footer.terms')}</a>
            <a href="/sitemap" className="hover:text-white transition-colors duration-300">{t('footer.sitemap')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
